/**
 * Research Tasks API client (M-X20c).
 *
 * Talks to /api/cards/:cardId/research-tasks (list/create/cost-estimate)
 * and /api/research-tasks/:id/* (read/start/abort/stream/import).
 *
 * Backend: apps/chaos-gateway/src/research.ts
 */
import { apiJson } from './client';

export type ResearchDepth = 'quick' | 'standard' | 'deep';

/**
 * Multi-vendor one-click (2026-07-11). The 3 CLI vendors run E2E today;
 * perplexity / deepseek / xai are API-key vendors whose HTTP adapters are
 * not built yet (blocked on keys) — the UI lists them as disabled chips.
 */
export type ResearchVendor =
  | 'anthropic'
  | 'openai'
  | 'google'
  | 'perplexity'
  | 'deepseek'
  | 'xai';

/** Display order for the vendor chip row. CLI vendors first. */
export const RESEARCH_VENDORS: ResearchVendor[] = [
  'anthropic',
  'openai',
  'google',
  'perplexity',
  'deepseek',
  'xai',
];

/** The 3 vendors that run over a direct HTTP API key (perplexity/deepseek/xai). */
export const RESEARCH_API_KEY_VENDORS: ResearchVendor[] = [
  'perplexity',
  'deepseek',
  'xai',
];

/**
 * Billing model per vendor — drives the cost strip in the Research form.
 *   subscription — anthropic (Claude) / openai (Codex) run through the founder's
 *     CLI OAuth subscription: there is NO per-call charge, so showing an
 *     estimated price for them is misleading.
 *   metered — google / perplexity / deepseek / xai run over a pay-per-use API
 *     key: only these contribute to the cost estimate.
 */
export type ResearchBilling = 'subscription' | 'metered';

export const RESEARCH_BILLING: Record<ResearchVendor, ResearchBilling> = {
  anthropic: 'subscription',
  openai: 'subscription',
  google: 'metered',
  perplexity: 'metered',
  deepseek: 'metered',
  xai: 'metered',
};

export type ResearchStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'aborted';

export interface ResearchCitation {
  index: number;
  url: string;
  title: string;
  snippet: string;
  fetchedAt: string;
}

export interface ResearchTask {
  id: string;
  cardId: string;
  sessionId: string | null;
  /** Shared id across the parallel runs of one multi-vendor launch (else null). */
  runGroupId: string | null;
  topic: string;
  depth: ResearchDepth;
  vendor: ResearchVendor;
  model: string | null;
  status: ResearchStatus;
  externalId: string | null;
  progressPct: number;
  currentActivity: string | null;
  finalReport: string | null;
  logPath: string | null;
  citations: ResearchCitation[] | null;
  tokensUsed: number;
  estimatedCostUsd: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  errorMessage: string | null;
}

export interface ResearchCostEstimate {
  estimatedTokens: number;
  estimatedCostUsd: string;
  estimatedDurationMin: string;
  vendor: ResearchVendor;
  depth: ResearchDepth;
  searchBudget: number;
  reportTargetWords: number;
}

export interface CreateResearchTaskBody {
  topic: string;
  depth: ResearchDepth;
  /** One row per vendor is created; N>1 launches N parallel runs. */
  vendors: ResearchVendor[];
  /** Applied only when exactly one vendor is selected. */
  model?: string;
}

export const researchTasksApi = {
  list(cardId: string): Promise<ResearchTask[]> {
    return apiJson<{ items: ResearchTask[] }>(
      `/cards/${cardId}/research-tasks`,
    ).then((r) => r.items);
  },
  costEstimate(
    cardId: string,
    params: { vendor: ResearchVendor; depth: ResearchDepth },
  ): Promise<ResearchCostEstimate> {
    const qs = new URLSearchParams({
      vendor: params.vendor,
      depth: params.depth,
    }).toString();
    return apiJson<ResearchCostEstimate>(
      `/cards/${cardId}/research-tasks/cost-estimate?${qs}`,
    );
  },
  /**
   * Create N research tasks (one per selected vendor) in a single call.
   * Returns the created tasks; the caller then starts each one.
   */
  create(cardId: string, body: CreateResearchTaskBody): Promise<ResearchTask[]> {
    return apiJson<{ tasks: ResearchTask[] }>(`/cards/${cardId}/research-tasks`, {
      method: 'POST',
      json: body,
    }).then((r) => r.tasks);
  },
  get(id: string): Promise<ResearchTask> {
    return apiJson<{ task: ResearchTask }>(`/research-tasks/${id}`).then(
      (r) => r.task,
    );
  },
  start(id: string): Promise<ResearchTask> {
    return apiJson<{ task: ResearchTask }>(`/research-tasks/${id}/start`, {
      method: 'POST',
    }).then((r) => r.task);
  },
  abort(id: string): Promise<void> {
    return apiJson(`/research-tasks/${id}/abort`, { method: 'POST' }).then(
      () => undefined,
    );
  },
  importToChat(
    id: string,
  ): Promise<{ sessionId: string; messageId: string | null }> {
    return apiJson<{ ok: true; sessionId: string; messageId: string | null }>(
      `/research-tasks/${id}/import-to-chat`,
      { method: 'POST' },
    ).then((r) => ({ sessionId: r.sessionId, messageId: r.messageId }));
  },
};

/**
 * Return the absolute SSE URL for a research task stream.
 * Import { env } from '../../env' and pass env.VITE_API_BASE_URL.
 * Not using apiJson because EventSource needs a plain URL string.
 */
export function researchTaskStreamUrl(apiBase: string, taskId: string): string {
  return `${apiBase.replace(/\/$/, '')}/research-tasks/${taskId}/stream`;
}

export const researchTasksKeys = {
  all: ['research-tasks'] as const,
  list: (cardId: string) => ['research-tasks', 'list', cardId] as const,
  detail: (id: string) => ['research-tasks', 'detail', id] as const,
  costEstimate: (cardId: string, vendor: string, depth: string) =>
    ['research-tasks', 'cost-estimate', cardId, vendor, depth] as const,
};
